import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { formatPercent } from "../../lib/dashboardMetrics";
import {
  Button,
  DataTable,
  EmptyState,
  Inline,
  InlineError,
  Skeleton,
  Text,
} from "../../ui";
import type { ReviewMarkerRow } from "./reviewListData";

export interface ReviewListTableProps {
  rows: ReviewMarkerRow[];
  isPending: boolean;
  error: string | null;
  onOpen: (markerId: string) => void;
  onOpenMarkers: () => void;
}

export function ReviewListTable({
  rows,
  isPending,
  error,
  onOpen,
  onOpenMarkers,
}: ReviewListTableProps) {
  const columns = useMemo<ColumnDef<ReviewMarkerRow>[]>(
    () => [
      {
        header: "Marker",
        accessorFn: (row) => row.summary.marker_display_name,
        cell: ({ row: { original } }) => (
          <Inline gap={2}>
            <Text as="span">{original.summary.marker_display_name}</Text>
            <Text as="span" variant="muted">
              {original.revision === null
                ? "Revision unavailable"
                : `rev ${String(original.revision)}`}
            </Text>
          </Inline>
        ),
      },
      {
        header: "Needs review",
        accessorFn: (row) => row.summary.needs_review_count,
      },
      {
        header: "Reviewed",
        accessorFn: (row) => row.summary.reviewed_count,
      },
      {
        header: "Agreement",
        accessorFn: (row) => row.agreement,
        cell: ({ row }) =>
          row.original.agreement === null
            ? "—"
            : formatPercent(row.original.agreement * 100),
      },
      {
        header: "Missed failures",
        accessorFn: (row) => row.missedFailures,
        cell: ({ row }) => row.original.missedFailures ?? "—",
      },
    ],
    [],
  );
  if (error) return <InlineError>{error}</InlineError>;
  if (isPending)
    return (
      <Skeleton label="Loading markers to review" rows={5} shape="table-row" />
    );
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No markers to review in this scope"
        action={<Button onClick={onOpenMarkers}>Open markers</Button>}
      />
    );
  }
  return (
    <DataTable
      ariaLabel="Markers to review"
      columns={columns}
      data={rows}
      getRowId={(row) => row.summary.marker_id}
      onRowClick={(row) => {
        onOpen(row.original.summary.marker_id);
      }}
      virtual={{ fill: true }}
    />
  );
}
